import useApiRequest from "@/lib/hooks/use-request";
import { formatDateTime } from "@/lib/hooks/utils";
import { DealDetailsResponseDto, DealHistoryResponseDto, DealHistoryType } from "@/lib/interfaces/deals";
import { Paginated } from "@/lib/interfaces/utils";
import { Button } from "@/ui-components/ui/button";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Textarea } from "@/ui-components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/ui-components/ui/tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import { Calendar, ChevronDown, ChevronRight, MessageSquare, User } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const DealHistory = ({
  item,
  setReload,
}: {
  item: DealDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();
  const [notes, setNotes] = useState<DealHistoryResponseDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const getNotes = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<Paginated<DealHistoryResponseDto>>(`deals/history/${item._id}`, "GET");
    setIsLoading(false);
    if (response.data) {
      setNotes(response.data.docs);
    }
  }, [apiRequest, item._id]);

  useEffect(() => {
    getNotes();
  }, [getNotes]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="mt-4 p-3">
      <DealForm item={item} setReload={setReload} />
      <hr className="my-4" />
      <div className="flex items-center mb-4 px-2">
        <button
          className="flex items-center text-sm font-medium text-gray-700 hover:text-primary"
          onClick={toggleExpanded}
        >
          {isExpanded ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
          Verlauf
        </button>
      </div>

      {isExpanded && (
        <div className="border border-gray-200 rounded-md p-3 bg-white">
          <div className="mt-6">
            <DataFetchParent
              isLoading={isLoading}
              data={notes}
              renderElement={(loadedNotes: DealHistoryResponseDto[]) => (
                <div className="relative pl-6 grid gap-4 grid-cols-[min-content_1fr] ">
                  {loadedNotes.length > 0 ? (
                    <>
                      {loadedNotes.map((note, index) => (
                        <HistoryItem
                          key={note._id}
                          note={note}
                          isLast={index === loadedNotes.length - 1}
                          isFirst={index === 0}
                        />
                      ))}
                    </>
                  ) : (
                    <div className="py-4 text-center text-gray-500 text-sm">Keine Einträge vorhanden</div>
                  )}
                </div>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface HistoryItemProps {
  note: DealHistoryResponseDto;
  isLast: boolean;
  isFirst: boolean;
}

const HistoryItem = ({ note, isLast, isFirst }: HistoryItemProps) => {
  // Determine icon based on note type
  const getIcon = () => {
    switch (note.type) {
      case DealHistoryType.HISTORY_NOTE:
      case DealHistoryType.NOTE:
        return <MessageSquare className="h-3 w-3" />;
      case DealHistoryType.ACTIVITY:
        return <Calendar className="h-3 w-3" />;
      default:
        return <MessageSquare className="h-3 w-3" />;
    }
  };

  // Format date from the createdAt timestamp
  const formattedDate = formatDateTime(note.createdAt);

  return (
    <>
      {/* Timeline bubble */}
      <div className="w-full relative flex justify-center">
        <div
          className={`flex items-center justify-center ${
            isFirst || isLast ? "w-5 h-5" : "w-8 h-8"
          } rounded-full bg-white border border-gray-300 z-10`}
        >
          <div className="flex items-center justify-center w-3.5 h-3.5 rounded-full">{getIcon()}</div>
        </div>
        {!isLast && (
          <div className="absolute border-l-2 border-dashed border-gray-200 top-0 mt-5 w-0.5 h-full bottom-0" />
        )}
      </div>

      <div className="w-full">
        <div className="bg-white rounded-md p-3 border border-gray-200 shadow-sm">
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="flex items-center mr-2">
                      <User className="h-3.5 w-3.5 mr-1 text-gray-500" />
                      <span className="text-xs font-medium">{note.belongsToUserFullName}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Erstellt von {note.belongsToUserFullName}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <span className="text-xs text-gray-500">{formattedDate}</span>
          </div>

          {note.historyNoteContent && (
            <div className="text-sm text-gray-800">{note.historyNoteContent.description}</div>
          )}
        </div>
      </div>
    </>
  );
};

const DealForm = ({
  item,
  setReload,
}: {
  item: DealDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();
  const schema = z.object({
    description: z.string().min(1),
  });

  type DealFormSchema = z.infer<typeof schema>;

  const form = useForm<DealFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: "",
    },
  });

  const createNote = async (data: DealFormSchema) => {
    await apiRequest<DealHistoryResponseDto>(`deals/history/${item._id}/notes`, "POST", {
      body: { description: data.description },
    });
    form.reset();
    setReload((prev) => !prev);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(createNote)} className="p-3">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Kommentar schreiben</FormLabel>
              <FormControl>
                <Textarea placeholder="Schreiben Sie einen Kommentar..." className="resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-row gap-2 mt-2 justify-end">
          <Button type="submit" size="sm">
            Erstellen
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default DealHistory;
